<script setup>
import { ref, onMounted, computed, watch } from "vue";
import CheckCard from "../components/CheckCard.vue";
import microphoneChecker from "../checkers/microphoneChecker.js";
import cameraChecker from "../checkers/cameraChecker.js";
import videoRecorderChecker from "../checkers/videoRecorderChecker.js";
import TurnTest from "./TurnTest.vue";
import turnConnectivityChecker from "../checkers/turnConnectivityChecker.js";
import { formatDateTimeForFileNaming, dateTimeFormat } from "@/utils";
import ecvServicesStatusChecker from "../checkers/ecvServicesStatusChecker.js";
import queueSocketConnectivityChecker from "../checkers/queueSocketConnectivityChecker.js";
import checkSignalingSocketConnectivity from "../checkers/signalingSocketChecker.js";
import ecvLogoSrc from "@/assets/images/logos/ecv.svg";
import i18n from "@/i18n";

const queryParams = new URLSearchParams(window.location.search);

const runTurnTest = ref({ start: false, check: null });
const turnTestComponent = ref({ stateLoading: null });

const checks = ref([
  {
    icon: "mdi-microphone",
    title: i18n.global.t("Microphone"),
    details: "Microphone: Test not started yet!",
    isStarted: false,
    isFinished: false,
    isPassed: false,
    isSelected: true,
    isExpanded: false,
    start: async function () {
      return await microphoneChecker(this);
    },
  },
  {
    icon: "mdi-camera",
    title: i18n.global.t("Camera"),
    details: "Camera: Test not started yet!",
    isStarted: false,
    isFinished: false,
    isPassed: false,
    isSelected: true,
    isExpanded: false,
    start: async function () {
      return await cameraChecker(this);
    },
  },
  {
    icon: "mdi-video-switch",
    title: i18n.global.t("TURNConnectivity"),
    details: "TURN Connectivity: Test not started yet!",
    isStarted: false,
    isFinished: false,
    isPassed: false,
    isSelected: true,
    isExpanded: false,
    start: async function () {
      turnTestComponent.value = { stateLoading: null };
      runTurnTest.value.start = true;
      runTurnTest.value.check = this;

      setTimeout(() => {
        this.isStarted = true;
        this.isFinished = false;
        this.isPassed = false;
      }, 100);
      return { isPassed: false, details: "TURN Connectivity: Test is in progress..." };
    },
    component: TurnTest,
  },
  {
    icon: "mdi-signal",
    title: i18n.global.t("SignalingSocketConnectivity"),
    details: "Signaling Socket Connectivity: Test not started yet!",
    isStarted: false,
    isFinished: false,
    isPassed: false,
    isSelected: true,
    isExpanded: false,
    start: async function () {
      return await checkSignalingSocketConnectivity(this);
    },
  },
]);

const isOnGoing = computed(() => {
  return checks.value.some((check) => check.isStarted && !check.isFinished);
});

const clientVersion = ref(queryParams.get("clientVersion"));
console.log("turnTestComponent?.value?.stateLoading", turnTestComponent?.value?.stateLoading);
watch(
  () => turnTestComponent?.value?.stateLoading,
  async (isLoading) => {
    if (typeof isLoading == "boolean" && !isLoading) {
      // if not state notloading, that means test is finished
      const turnTestResult = await turnConnectivityChecker(runTurnTest.value.check, turnTestComponent.value.turnServers);
      runTurnTest.value.check.isPassed = turnTestResult.isPassed;
      runTurnTest.value.check.details =
        turnTestResult.details + (turnTestResult.isPassed ? "" : ` - ${turnTestComponent.value.stateDetails}`);
      runTurnTest.value.check.isFinished = true;
      runTurnTest.value.check.isSelected = !turnTestResult.isPassed;
      runTurnTest.value.start = false;
    }
  },
);

/*
let emitData = {
    stunServers: stunServers.value,
    turnServers: turnServers.value,
    details: stateDetails.value,
    overallStatus:
      turnServers.value.every((s) => s.status === "ok") && stunServers.value.every((s) => s.status === "ok") ? "ok" : "error",
  };

*/
function turnStatusUpdated(emitData) {
  if (!emitData || !emitData.turnServers || !emitData.stunServers) {
    return;
  }

  if (emitData.overallStatus == "ok") {
    runTurnTest.value.check.isPassed = true;
    runTurnTest.value.check.details = `TURN Connectivity: All servers are working properly!`;
  } else {
    runTurnTest.value.check.isPassed = false;
    runTurnTest.value.check.details = `TURN Connectivity: Some servers are not working properly!`;
  }

  runTurnTest.value.check.details += `\n\nSTUN Servers:\n${emitData.stunServers
    .map((s) => `- ${s.url}: ${s.status}`)
    .join("\n")}`;
  runTurnTest.value.check.details += `\n\nTURN Servers:\n${emitData.turnServers
    .map((s) => `- ${s.domain}: ${s.status}`)
    .join("\n")}`;
  runTurnTest.value.check.isFinished = true;
  runTurnTest.value.check.isSelected = !runTurnTest.value.check.isPassed;
  runTurnTest.value.start = false;
}

async function start() {
  if (!isOnGoing.value) {
    for (let check of checks.value) {
      if (check.isSelected) {
        (async () => {
          // Init
          check.isStarted = false;
          check.isFinished = false;
          check.isPassed = false;
          check.isExpanded = false;
          check.details = `${check.title}: Test is in progress...`;

          // Started
          check.isStarted = true;

          try {
            // Result
            const result = await check.start();
            check.isPassed = result.isPassed;
            check.details = result.details;
          } catch (error) {
            // Error
            console.error(error);
            check.isPassed = false;
            check.details = `${check.title} Error: ${error.message}`;
          }

          // Finished
          check.isFinished = true;
          check.isSelected = !check.isPassed;
        })();
      }
    }
  }
}

async function exportDetails() {
  if (isOnGoing.value) {
    return;
  }

  let fileContent = checks.value.map((item) => "- " + item.details).join("\n");
  fileContent = `- Date & Time: ${dateTimeFormat(new Date().toISOString())} \n${fileContent}`;
  const blob = new Blob([fileContent], { type: "text/plain" });

  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `StatusCheck-${formatDateTimeForFileNaming()}.txt`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

onMounted(() => {
  if (!clientVersion.value) {
    document.getElementById("todaysYear").innerHTML = new Date().getFullYear();
  }

  const startCheck = queryParams.get("start");

  if (clientVersion.value) {
    checks.value.unshift({
      icon: "mdi-information",
      title: i18n.global.t("ClientStatus"),
      details: "Client Status: Test not started yet!",
      isStarted: false,
      isFinished: false,
      isPassed: false,
      isSelected: true,
      isExpanded: false,
      start: async function () {
        console.log(`${this.title} Check Started`);

        let reply = { isPassed: false, details: "Not Working!" };
        let clientChecksResults = [];

        let agentUsername = queryParams.get("agentUsername");
        let agentLicense = queryParams.get("agentLicense");
        let agentSocketConnectivity = queryParams.get("agentSocketConnectivity");
        let videoRecorder = await videoRecorderChecker(this);
        let videoProcessorConnectivity = queryParams.get("videoProcessorConnectivity");
        let webRtcIpHandlingPolicy = queryParams.get("webRtcIpHandlingPolicy");
        let webRtcUdpPortRangePolicy = queryParams.get("webRtcUdpPortRangePolicy");

        if (agentLicense) {
          agentLicense = !isNaN(agentLicense) && Number(agentLicense) ? true : false;
          clientChecksResults.push({
            title: "Agent License",
            details: agentLicense ? "Working properly." : "Not working properly!",
            isPassed: agentLicense,
          });
        }

        if (agentSocketConnectivity) {
          agentSocketConnectivity = !isNaN(agentSocketConnectivity) && Number(agentSocketConnectivity) ? true : false;
          clientChecksResults.push({
            title: "Agent Socket Connectivity",
            details: agentSocketConnectivity ? "Working properly." : "Not working properly!",
            isPassed: agentSocketConnectivity,
          });
        }

        clientChecksResults.push({
          title: "Video Recorder",
          details: videoRecorder.details.replaceAll(this.title + ": ", ""),
          isPassed: videoRecorder.isPassed,
        });

        if (videoProcessorConnectivity) {
          videoProcessorConnectivity = !isNaN(videoProcessorConnectivity) && Number(videoProcessorConnectivity) ? true : false;
          clientChecksResults.push({
            title: "Video Processor Connectivity",
            details: videoProcessorConnectivity ? "Working properly." : "Not working properly!",
            isPassed: videoProcessorConnectivity,
          });
        }

        if (webRtcIpHandlingPolicy) {
          webRtcIpHandlingPolicy = !isNaN(webRtcIpHandlingPolicy) && Number(webRtcIpHandlingPolicy) ? true : false;
          clientChecksResults.push({
            title: "WebRTC IP Handling Policy",
            details: webRtcIpHandlingPolicy ? "Ok." : "Not ok!",
            isPassed: webRtcIpHandlingPolicy,
          });
        }

        if (webRtcUdpPortRangePolicy) {
          webRtcUdpPortRangePolicy = !isNaN(webRtcUdpPortRangePolicy) && Number(webRtcUdpPortRangePolicy) ? true : false;
          clientChecksResults.push({
            title: "WebRTC UDP Port Range Policy",
            details: webRtcUdpPortRangePolicy ? "Ok." : "Not ok!",
            isPassed: webRtcUdpPortRangePolicy,
          });
        }

        reply.isPassed = clientChecksResults.every((item) => item.isPassed);

        let details = {};
        details["Client Version"] = clientVersion.value;
        if (agentUsername) details["Agent Username"] = agentUsername;
        clientChecksResults.forEach((item) => {
          details[item.title] = item.details;
        });

        reply.details = `${this.title}: \n${Object.keys(details)
          .map((key) => `\t- ${key}: ${details[key]}`)
          .join("\n")}`;
        return reply;
      },
    });

    checks.value.push({
      icon: "mdi-server-network",
      title: i18n.global.t("ECVServicesStatus"),
      details: "ECV Services Status: Test not started yet!",
      isStarted: false,
      isFinished: false,
      isPassed: false,
      isSelected: true,
      isExpanded: false,
      start: async function () {
        return await ecvServicesStatusChecker(this);
      },
    });
  } else {
    checks.value.push({
      icon: "mdi-network",
      title: i18n.global.t("QueueSocketConnectivity"),
      details: "Queue Socket Connectivity: Test not started yet!",
      isStarted: false,
      isFinished: false,
      isPassed: false,
      isSelected: true,
      isExpanded: false,
      start: async function () {
        return await queueSocketConnectivityChecker(this);
      },
    });
  }

  if (startCheck) {
    start();
  }
});
</script>

<template>
  <v-sheet class="global-bg" rounded>
    <v-card id="header">
      <v-row>
        <v-col cols="2">
          <v-img id="logo" :src="ecvLogoSrc"></v-img>
        </v-col>

        <v-col cols="7">
          <v-card-title class="text-h5 text-left">
            {{ $t("StatusCheck") }}
            <span v-if="clientVersion" class="client-version-text">{{ clientVersion }}</span>
          </v-card-title>
        </v-col>

        <v-col cols="3">
          <v-btn-group style="float: right">
            <v-btn @click="exportDetails()" class="mr-1" color="info" icon="mdi-export"></v-btn>
            <v-btn @click="start()" style="flex-grow: 1" :color="isOnGoing ? 'warning' : 'success'">{{
              isOnGoing ? $t("Started") : $t("Start")
            }}</v-btn>
          </v-btn-group>
        </v-col>
      </v-row>
    </v-card>

    <CheckCard
      v-for="check in checks"
      :key="check"
      :icon="check.icon"
      :title="check.title"
      :details="check.details"
      :isStarted="check.isStarted"
      :isFinished="check.isFinished"
      :isPassed="check.isPassed"
      :isSelected="check.isSelected"
      :isExpanded="check.isExpanded"
      @onSelected="
        (isSelected) => {
          check.isSelected = isSelected;
        }
      "
      @onExpanded="
        (isExpanded) => {
          check.isExpanded = isExpanded;
        }
      "
      :component="check.component"
      :isSelectable="check.isSelectable"
      :showComponentOnlyWhenFailed="check.showComponentOnlyWhenFailed"></CheckCard>

    <footer v-if="!clientVersion" id="footer">
      {{ $t("Copyright") }} © 2011 - <span id="todaysYear">2024</span> CCR. {{ $t("AllRightsReserved") }}
    </footer>

    <TurnTest v-if="runTurnTest.start" ref="turnTestComponent" style="display: none" @update:modelValue="turnStatusUpdated" />
  </v-sheet>
</template>

<style scoped>
#header {
  max-width: 1000px;
  margin: 0 auto;
  padding: 20px;
  margin-top: 10px;
  margin-bottom: 20px;
}

#logo {
  margin-left: 6px;
  padding-top: 6px;
  width: 120px;
}

#footer {
  position: fixed;
  left: 0;
  bottom: 0;

  width: 100%;
  text-align: center;

  font-family: "Roboto", sans-serif;
  font-weight: 300;

  border-top: rgba(0, 0, 0, 0.12) 1px solid;
  padding-top: 4px;
  padding-bottom: 6px;
  background-color: #212121;
  color: white;
}

.v-sheet {
  overflow: auto;
  padding-bottom: 100px !important;
}

.client-version-text {
  display: block;
  line-height: 1; /* Or adjust as needed */
  margin: 0;
  padding: 0;
  font-size: 12px;
  font-weight: 300;
  color: #888;
  font-family: "Roboto", sans-serif;
  font-style: italic;
}
</style>
